import { useState } from 'react';
import { Package } from 'lucide-react';
import type { CategoriaVista, PaqueteVista, ProductoVista } from '@shared/types';
import { formatPrecio } from '@renderer/utils/formatters';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
// Sprint 80 — La misma regla de agotado que usa el POS al agregar al carrito.
import { estaAgotado, estaBajo, stockDisponible } from '@shared/stockProducto';

interface Props {
  productos: ProductoVista[];
  paquetes: PaqueteVista[];
  categorias: CategoriaVista[];
  onClickProducto: (p: ProductoVista) => void;
  onClickPaquete: (p: PaqueteVista) => void;
}

export function MenuGrid({ productos, paquetes, categorias, onClickProducto, onClickPaquete }: Props) {
  const negocio = useNegocioStore((s) => s.negocio);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  const [categoriaActiva, setCategoriaActiva] = useState<number | 'todas'>('todas');

  /*
    Sprint 73 — Aquí vivía un `useEffect` vacío con un comentario que decía lo que NO hacía. El
    default es "Todas" y se queda así a propósito: es la única pestaña donde aparece un paquete o un
    producto sin categoría. El efecto muerto sólo hacía dudar de si algo se autoseleccionaba.
  */

  const productosFiltrados =
    categoriaActiva === 'todas'
      ? productos
      : productos.filter((p) => p.categoriaId === categoriaActiva);
  const paquetesFiltrados =
    categoriaActiva === 'todas'
      ? paquetes
      : paquetes.filter((p) => p.categoriaId === categoriaActiva);

  return (
    <div className="flex h-full flex-col">
      {/* Tabs de categorías */}
      <div className="flex flex-wrap gap-2 border-b border-slate-200 bg-white px-4 py-2">
        <CategoriaButton
          activo={categoriaActiva === 'todas'}
          onClick={() => setCategoriaActiva('todas')}
        >
          Todas
        </CategoriaButton>
        {categorias.map((c) => (
          <CategoriaButton
            key={c.id}
            activo={categoriaActiva === c.id}
            color={c.color ?? undefined}
            onClick={() => setCategoriaActiva(c.id)}
          >
            {c.nombre}
          </CategoriaButton>
        ))}
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto p-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {paquetesFiltrados.map((p) => {
            // Sprint 44 — stockEfectivo === 0 ⇒ algún renglón obligatorio sin
            // opciones disponibles. null = no aplica guard (sin insumos
            // ligados). En ambos casos no agotamos por silencio.
            const paqueteAgotado = p.stockEfectivo === 0;
            /**
             * Sprint 60 — Un paquete agotado por falta de UN renglón sí se puede
             * abrir: dentro se elige qué servir en su lugar. Bloquearlo era lo que
             * convertía la falta de un refresco en una venta perdida.
             */
            const resoluble = paqueteAgotado && Boolean(p.motivoAgotado);
            /*
              Sprint 74 — Fuera de su horario, el paquete no se puede tocar.

              El veredicto lo calculó el SERVIDOR (`disponibleAhora`), no esta pantalla: el reloj del
              navegador no es el del negocio y el renderer no es autoridad. `pedidoService` rechaza la
              venta con el mismo texto, pero dejar el botón activo sería peor — el mesero ya le
              prometió al cliente la Promo Amigos antes de descubrir que no se puede cobrar.

              `=== false` y no `!`: en los consumidores que no piden el campo llega `undefined`, y eso
              significa "no se evaluó", no "no disponible".
            */
            const fueraDeHorario = p.disponibleAhora === false;
            const disabled = !p.habilitado || fueraDeHorario || (paqueteAgotado && !resoluble);
            // Sprint 60 — El motivo viaja con el paquete. Un "AGOTADO" mudo hace que
            // el operador revise el inventario, vea existencias de sobra y concluya
            // que el sistema está mal; el culpable suele ser otro renglón.
            // Sprint 74 — El horario gana al agotado: si ni se vende a esta hora, el stock sobra.
            const motivo = fueraDeHorario
              ? p.motivoNoDisponible
              : paqueteAgotado
                ? p.motivoAgotado
                : null;
            return (
              <button
                key={`paq-${p.id}`}
                onClick={() => onClickPaquete(p)}
                disabled={disabled}
                aria-label={
                  fueraDeHorario
                    ? `Paquete ${p.nombre}. ${motivo ?? 'No disponible a esta hora'}`
                    : paqueteAgotado
                    ? `Paquete ${p.nombre}. ${motivo ?? 'Agotado'}${resoluble ? '. Toca para elegir un reemplazo' : ''}`
                    : p.habilitado
                    ? `Paquete ${p.nombre}, ${formatPrecio(p.precioBase, monedaSimbolo, locale)}`
                    : `Paquete ${p.nombre} no disponible`
                }
                className={`card flex flex-col items-start gap-2 text-left transition ${
                  disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : resoluble
                      ? 'border-amber-300 hover:border-primary hover:shadow-md'
                      : 'hover:border-primary hover:shadow-md'
                }`}
              >
                <div className="flex h-10 w-10 items-center justify-center rounded bg-purple-100 text-purple-600">
                  <Package className="h-5 w-5" />
                </div>
                <div className="font-semibold leading-tight">{p.nombre}</div>
                {motivo && (
                  <div
                    className={`text-xs font-medium leading-tight ${
                      fueraDeHorario ? 'text-slate-600' : 'text-amber-700'
                    }`}
                  >
                    {motivo}
                    {resoluble && (
                      <span className="mt-0.5 block font-semibold text-primary">
                        Toca para servir otra cosa
                      </span>
                    )}
                  </div>
                )}
                <div className="text-xs text-slate-500 line-clamp-2 min-h-[2rem]">
                  {p.descripcion ?? 'Paquete personalizable'}
                </div>
                <div className="font-mono font-bold text-primary">
                  {formatPrecio(p.precioBase, monedaSimbolo, locale)}
                </div>
                <div className="flex items-center justify-between w-full">
                  <span className="text-xs uppercase tracking-wide text-purple-600 font-semibold">
                    Paquete
                  </span>
                  {paqueteAgotado && (
                    <span className="text-xs uppercase tracking-wide text-orange-600 font-bold">
                      Agotado
                    </span>
                  )}
                </div>
              </button>
            );
          })}

          {productosFiltrados.map((p) => {
            // Sprint 14.2 — stockEfectivo/stockMinimoEfectivo vienen del backend
            // ya derivados del insumo (cuando aplica).
            //
            // Sprint 62 — `null`/`undefined` = el producto no lleva control de
            // inventario, así que NUNCA está agotado. Antes se comparaba contra
            // `inventarioActual` (0 en todo producto nuevo) y el botón salía
            // deshabilitado con la etiqueta "Agotado": imposible de vender e
            // imposible de entender.
            // Sprint 80 — La misma función que usa el POS al agregar. Estaban escritas por
            // separado y sólo se corrigió una: la tarjeta se dejaba tocar y el clic rebotaba.
            const stock = stockDisponible(p);
            const agotado = estaAgotado(p);
            const bajo = estaBajo(p);
            return (
              <button
                key={`prod-${p.id}`}
                onClick={() => onClickProducto(p)}
                disabled={!p.habilitado || agotado}
                aria-label={
                  agotado
                    ? `${p.nombre} — agotado`
                    : !p.habilitado
                      ? `${p.nombre} — no disponible`
                      : `${p.nombre}, ${formatPrecio(p.precio, monedaSimbolo, locale)}` +
                        (stock !== null ? `, quedan ${stock}` : '')
                }
                className={`card flex flex-col items-start gap-2 text-left transition ${
                  agotado || !p.habilitado
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:border-primary hover:shadow-md'
                }`}
              >
                <div className="font-semibold leading-tight">{p.nombre}</div>
                <div className="font-mono font-bold text-primary">
                  {formatPrecio(p.precio, monedaSimbolo, locale)}
                </div>
                {/*
                  Sprint 63 (Fase 3) — La disponibilidad, a la vista y en palabras.
                  Antes decía "Stock: 8" —y con un producto sin control, "Stock: 0"—.
                  El mesero necesita saber cuántas puede prometer sin preguntarle a
                  nadie: con 2 en la mano ya avisa a la cocina, en vez de descubrirlo
                  cuando ya no hay. Y si el producto no lleva control, no se dice nada:
                  un número inventado es peor que ninguno.
                */}
                {agotado ? (
                  <span className="rounded bg-slate-200 px-2 py-0.5 text-xs font-bold uppercase text-slate-700">
                    Agotado
                  </span>
                ) : stock !== null ? (
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-semibold ${
                      bajo ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {bajo ? `¡Sólo quedan ${stock}!` : `Quedan ${stock}`}
                  </span>
                ) : null}
              </button>
            );
          })}

          {productosFiltrados.length === 0 && paquetesFiltrados.length === 0 && (
            <div className="col-span-full py-12 text-center text-slate-400">
              No hay productos en esta categoría.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoriaButton({
  activo,
  color,
  onClick,
  children,
}: {
  activo: boolean;
  color?: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition ${
        activo ? 'bg-primary text-primary-fg' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
      }`}
      style={activo && color ? { backgroundColor: color, color: 'white' } : undefined}
    >
      {children}
    </button>
  );
}
